import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, CalendarClock, ChevronLeft, ChevronRight, Loader2, Video } from "lucide-react";
import { toast } from "sonner";

const EN = (new URLSearchParams(window.location.search).get("lang") || navigator.language || "es")
  .toLowerCase().startsWith("en");
const C = EN
  ? { yours: "Your booking", with: "With", cancelled: "This booking is cancelled. If you want another time, open the booking page again.",
      join: "Join the meeting", reschedule: "Reschedule", cancel: "Cancel booking",
      pick: "Pick a new time", shown: "Times are shown in your local time", none: "No times available right now.",
      notFound: "We could not find this booking.", moved: "Booking moved", done: "Booking cancelled",
      otherDay: "Pick another day" }
  : { yours: "Tu reserva", with: "Con", cancelled: "Esta reserva está cancelada. Si querés otro espacio, entrá de nuevo a la página de reservas.",
      join: "Entrar a la reunión", reschedule: "Reprogramar", cancel: "Cancelar reserva",
      pick: "Elegí un nuevo horario", shown: "Se muestran en tu hora local", none: "No hay espacios disponibles por ahora.",
      notFound: "No encontramos esta reserva.", moved: "Reserva movida", done: "Reserva cancelada",
      otherDay: "Elegir otro día" };
const LOC = EN ? "en-US" : "es-CR";

const guessTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "America/Costa_Rica"; }
};

/** Guest self-service: cancel or move an existing booking (link sent by email). */
export default function BookingManage() {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [days, setDays] = useState([]);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [pickedDay, setPickedDay] = useState(null);
  const tz = booking?.guest_timezone || guessTz();

  const load = () =>
    api.get(`/public/booking/manage/${token}`).then((r) => setBooking(r.data)).catch(() => setBooking(false));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const openSlots = async () => {
    setPicking(true);
    setPickedDay(null);
    const r = await api.get(`/public/booking/${booking.slug}/slots`, { params: { type_id: booking.type_id } });
    setDays(r.data.days || []);
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/public/booking/manage/${token}/cancel`);
      toast.success(C.done);
      load();
    } finally { setBusy(false); }
  };

  const move = async (start) => {
    setBusy(true);
    try {
      await api.post(`/public/booking/manage/${token}/reschedule`, { start });
      toast.success(C.moved);
      setPicking(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo mover");
    } finally { setBusy(false); }
  };

  const localDays = {};
  for (const d of days) {
    for (const s of d.slots) {
      const key = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(s));
      (localDays[key] = localDays[key] || []).push(s);
    }
  }
  const WEEK = EN ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] : ["lun", "mar", "mié", "jue", "vie", "sáb", "dom"];
  const grid = (() => {
    const offset = (new Date(month.y, month.m, 1).getDay() + 6) % 7;
    const total = new Date(month.y, month.m + 1, 0).getDate();
    const cells = Array(offset).fill(null);
    for (let d = 1; d <= total; d++) {
      const key = `${month.y}-${String(month.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ day: d, key, count: (localDays[key] || []).length });
    }
    return cells;
  })();

  if (booking === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
        <p className="text-sm text-slate-600" data-testid="manage-not-found">{C.notFound}</p>
      </div>
    );
  }
  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="h-6 w-6 animate-spin text-[#004aad]" />
      </div>
    );
  }

  const when = new Date(booking.start_utc).toLocaleString(LOC, {   // tz-ok: se muestra en la zona del visitante
    dateStyle: "full", timeStyle: "short", timeZone: tz,
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50/30">
      <div className="max-w-2xl mx-auto px-4 py-12 sm:py-16">

        <div
          className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_32px_rgba(0,0,0,0.06)] p-6 sm:p-10"
          data-testid="manage-card"
        >
          <p className="text-xs tracking-[0.1em] uppercase font-semibold text-slate-500">{C.yours}</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#004aad] mt-2">{booking.type_name}</h1>
          <p className="text-base text-slate-600 mt-3">{C.with} {booking.host_name}</p>
          <p className="text-lg sm:text-xl text-slate-900 font-medium mt-4 flex items-start gap-2">
            <CalendarClock className="h-5 w-5 text-[#ff731d] mt-1 shrink-0" />
            <span>{when}<span className="block text-sm font-normal text-slate-500 mt-1">{tz.replace("_", " ")}</span></span>
          </p>

          {booking.status === "cancelled" ? (
            <p className="mt-8 rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700" data-testid="manage-cancelled">
              {C.cancelled}
            </p>
          ) : (
            <div className="flex flex-wrap gap-3 mt-8">
              {booking.meet_link && (
                <a
                  href={booking.meet_link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 min-h-[44px] rounded-full bg-[#004aad] px-6 text-sm font-semibold text-white hover:bg-[#003a8c] transition-colors duration-200"
                  data-testid="manage-meet-link"
                >
                  <Video className="h-4 w-4" /> {C.join}
                </a>
              )}
              <button
                onClick={openSlots}
                disabled={busy}
                data-testid="manage-reschedule-btn"
                className="inline-flex items-center justify-center min-h-[44px] rounded-full border border-slate-200 bg-white px-6 text-sm font-semibold text-slate-700 hover:border-[#004aad] hover:text-[#004aad] transition-colors duration-200"
              >
                {C.reschedule}
              </button>
              <button
                onClick={cancel}
                disabled={busy}
                data-testid="manage-cancel-btn"
                className="inline-flex items-center justify-center min-h-[44px] rounded-full px-6 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors duration-200"
              >
                {C.cancel}
              </button>
            </div>
          )}
        </div>

        {picking && (
          <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_32px_rgba(0,0,0,0.06)] p-5 sm:p-8 mt-8"
               data-testid="manage-slots">
            <h2 className="text-base md:text-lg font-semibold text-slate-900">{C.pick}</h2>
            <p className="text-sm text-slate-500 mt-1">{C.shown} ({tz.replace("_", " ")}).</p>

            {pickedDay ? (
              <>
                <button onClick={() => setPickedDay(null)} data-testid="manage-back-to-calendar"
                        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#004aad] mt-5">
                  <ArrowLeft className="h-4 w-4" /> {C.otherDay}
                </button>
                <p className="font-display text-xl font-bold text-[#004aad] mt-3">
                  {new Date(`${pickedDay}T12:00:00Z`).toLocaleDateString(LOC, {   // tz-ok: mediodía fijo + timeZone UTC
                    weekday: "long", day: "numeric", month: "long", timeZone: "UTC" })}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-5">
                  {(localDays[pickedDay] || []).map((s) => (
                    <button key={s} onClick={() => move(s)} disabled={busy}
                            data-testid={`manage-slot-${s}`}
                            className="min-h-[52px] rounded-xl border border-[#004aad]/25 bg-white text-base font-semibold text-[#004aad] hover:bg-[#004aad] hover:text-white transition-colors">
                      {new Date(s).toLocaleTimeString(LOC, { hour: "2-digit", minute: "2-digit", timeZone: tz })}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mt-5">
                  <button onClick={() => setMonth(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }))}
                          data-testid="manage-prev-month"
                          className="h-11 w-11 grid place-items-center rounded-full hover:bg-slate-100 text-slate-500">
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <p className="text-base md:text-lg font-semibold text-slate-900">
                    {new Date(month.y, month.m, 1).toLocaleDateString(LOC, { month: "long", year: "numeric" })}{/* tz-ok: mes armado con números */}
                  </p>
                  <button onClick={() => setMonth(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }))}
                          data-testid="manage-next-month"
                          className="h-11 w-11 grid place-items-center rounded-full hover:bg-slate-100 text-slate-500">
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1 mt-4 text-center text-xs uppercase tracking-wider text-slate-400">
                  {WEEK.map((w) => <div key={w} className="py-1">{w}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1 mt-1" data-testid="manage-calendar">
                  {grid.map((cell, i) => cell === null ? <div key={`x${i}`} /> : (
                    <button key={cell.key} disabled={!cell.count} onClick={() => setPickedDay(cell.key)}
                            data-testid={`manage-day-${cell.key}`}
                            className={`relative h-12 rounded-xl text-base transition-colors ${
                              cell.count ? "font-bold text-[#004aad] border border-[#004aad]/30 hover:bg-[#004aad] hover:text-white"
                                         : "text-slate-300"}`}>
                      {cell.day}
                      {!!cell.count && <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 h-1.5 w-1.5 rounded-full bg-[#ff731d]" />}
                    </button>
                  ))}
                </div>
                {Object.keys(localDays).length === 0 && (
                  <p className="text-sm text-slate-500 mt-5">{C.none}</p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
